import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/StatusPill";
import { Calendar, Clock, MapPin, ShieldCheck, Star, Send, FileText, Map } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function DispatcherRequestDetail() {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [facility, setFacility] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState([]);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [proposalForm, setProposalForm] = useState({
    professional_cost: 0, markup_percent: 25, urgency_premium: 0,
    weekend_premium: 0, holiday_premium: 0, travel_charges: 0, notes: "",
  });

  const load = () => {
    api.get(`/requests/${id}`).then((r) => {
      setRequest(r.data);
      api.get(`/facilities/${r.data.facility_id}`).then((f) => setFacility(f.data));
      // Default proposal cost
      const baseCost = r.data.total_hours * 30 * r.data.count;
      setProposalForm((p) => ({ ...p, professional_cost: baseCost }));
    });
    api.get(`/requests/${id}/matches`).then((m) => setMatches(m.data));
  };
  useEffect(() => { load(); }, [id]);

  const toggle = (pid) => setSelected((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);

  const sendOffers = async () => {
    try {
      const { data } = await api.post(`/requests/${id}/offers`, { professional_ids: selected });
      toast.success(`Sent ${data.sent} offers`);
      setSelected([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitProposal = async () => {
    try {
      const subtotal = Number(proposalForm.professional_cost) + Number(proposalForm.professional_cost) * Number(proposalForm.markup_percent) / 100
        + Number(proposalForm.urgency_premium) + Number(proposalForm.weekend_premium) + Number(proposalForm.holiday_premium) + Number(proposalForm.travel_charges);
      await api.post("/proposals", { request_id: id, ...proposalForm });
      toast.success(`Proposal sent — Total: $${(subtotal * 1.13).toFixed(2)}`);
      setProposalOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!request) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="dispatcher-request-detail">
      <Link to="/dispatch/requests" className="text-sm text-slate-500 hover:text-brand-navy">← Back to requests</Link>
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>{request.position} × {request.count}</h1>
              <div className="mt-2 flex items-center flex-wrap gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{facility?.name}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{request.shift_date}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{request.start_time}-{request.end_time} · {request.total_hours}h · {request.shift_type}</span>
              </div>
              {request.notes && <p className="mt-3 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{request.notes}</p>}
            </div>
            <div className="flex flex-col gap-2 items-end">
              <StatusPill status={request.status} />
              <span className={`status-pill ${request.urgency === "emergency" ? "bg-red-100 text-red-700" : request.urgency === "urgent" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>{request.urgency}</span>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Dialog open={proposalOpen} onOpenChange={setProposalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-navy hover:bg-brand-blue" data-testid="open-proposal-dialog"><FileText className="w-4 h-4 mr-1" />Build proposal</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Build Proposal</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  {[
                    ["professional_cost", "Professional cost (CAD)"],
                    ["markup_percent", "Markup %"],
                    ["urgency_premium", "Urgency premium"],
                    ["weekend_premium", "Weekend premium"],
                    ["holiday_premium", "Holiday premium"],
                    ["travel_charges", "Travel charges"],
                  ].map(([k, l]) => (
                    <div key={k}>
                      <Label>{l}</Label>
                      <Input type="number" value={proposalForm[k]} onChange={(e) => setProposalForm({ ...proposalForm, [k]: e.target.value })} data-testid={`proposal-${k}`} />
                    </div>
                  ))}
                  <Textarea placeholder="Notes" value={proposalForm.notes} onChange={(e) => setProposalForm({ ...proposalForm, notes: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button onClick={submitProposal} className="bg-brand-navy hover:bg-brand-blue" data-testid="submit-proposal-btn">Send Proposal</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {selected.length > 0 && (
              <Button onClick={sendOffers} className="bg-brand-teal hover:bg-brand-teal/90" data-testid="send-offers-btn">
                <Send className="w-4 h-4 mr-1" /> Send offers to {selected.length}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Smart-matched professionals</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-2">
            {matches.map((m, idx) => (
              <div
                key={m.id}
                className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${selected.includes(m.id) ? "border-brand-teal bg-teal-50/50" : "border-slate-200 hover:border-slate-300"}`}
                onClick={() => toggle(m.id)}
                data-testid={`match-${m.id}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} className="w-4 h-4 accent-teal-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{m.first_name} {m.last_name}</div>
                    <div className="text-xs text-slate-500">{m.primary_position} · {m.experience_years} yrs · {m.specialties?.[0]}</div>
                  </div>
                  <div className="flex gap-4 text-xs text-slate-600">
                    <div className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" />{m.netstaff_score}</div>
                    <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{m.distance_km}km</div>
                    {m.available_now && <span className="text-green-600 font-semibold flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Avail now</span>}
                  </div>
                  <div className="px-3 py-1 bg-brand-navy text-white rounded-full text-xs font-bold">
                    {m.match_score}
                  </div>
                </div>
              </div>
            ))}
            {matches.length === 0 && <div className="text-center py-12 text-slate-500">No matching professionals found.</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
